import {
    EnumFilterExpressionOperation,
    EnumOperation,
    type FilterCondition,
    type FilterExpression,
    type ListRequest,
} from '@synqo/frontend-component-library';
import { NO_AVV_LABEL, type StockRow, type StockView } from './Stock/stockView';

export const STORAGE_JOURNAL_TABLE = 'StorageTransactionList';

const STORAGE_LOCATION_KEY = 'storage_location.foreign_key';
const MATERIAL_KEY = 'material.foreign_key';
const AVV_KEY = 'avv.foreign_key';
const AVV_COLUMN = 'avv.display_string';
const OCCURRED_AT_COLUMN = 'occurred_at';
const CREATED_AT_COLUMN = 'created_at';

const STORAGE_LOCATION_PARAM = 'storage_location';
const MATERIAL_PARAM = 'material';
const AVV_PARAM = 'avv';
const LABEL_PARAM = 'scope';
const NO_AVV_PARAM = 'none';

/** The query string that opens the journal on one Material or AVV of a Lagerort. */
// @ai-generated 2026-10-26T11:20:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function journalSearch(storageLocation: { id: string; name: string }, view: StockView, row: StockRow) {
    const params = new URLSearchParams({ [STORAGE_LOCATION_PARAM]: storageLocation.id });
    if (view === 'material') {
        params.set(MATERIAL_PARAM, row.id);
    } else {
        params.set(AVV_PARAM, row.id || NO_AVV_PARAM);
    }
    params.set(LABEL_PARAM, `${storageLocation.name} · ${row.label}`);
    return `?${params.toString()}`;
}

const equal = (field: string, value: string): FilterCondition => ({ field, value, operator: EnumOperation.Equal });

/** The exact-match conditions of the Bestand row the journal was opened for, none for a plain journal. */
// @ai-generated 2026-10-26T11:20:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function scopeConditions(params: URLSearchParams): FilterCondition[] {
    const storageLocation = params.get(STORAGE_LOCATION_PARAM);
    if (!storageLocation) {
        return [];
    }

    const conditions = [equal(STORAGE_LOCATION_KEY, storageLocation)];
    const material = params.get(MATERIAL_PARAM);
    if (material) {
        conditions.push(equal(MATERIAL_KEY, material));
    }
    const avv = params.get(AVV_PARAM);
    if (avv === NO_AVV_PARAM) {
        conditions.push(equal(AVV_COLUMN, ''));
    } else if (avv) {
        conditions.push(equal(AVV_KEY, avv));
    }
    return conditions;
}

/** The Lagerort and Material the journal is limited to, as a new booking starts with them. */
// @ai-generated 2026-10-27T11:30:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function scopePrefill(params: URLSearchParams) {
    const storageLocationId = params.get(STORAGE_LOCATION_PARAM) ?? undefined;
    return { storageLocationId, materialId: storageLocationId ? params.get(MATERIAL_PARAM) ?? undefined : undefined };
}

/** The text that names the Bestand row the journal is limited to. */
// @ai-generated 2026-10-26T11:20:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function scopeLabel(params: URLSearchParams) {
    return params.get(LABEL_PARAM) ?? undefined;
}

/** The query string without the scope of a Bestand row. */
// @ai-generated 2026-10-26T11:20:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function withoutScope(params: URLSearchParams) {
    const rest = new URLSearchParams(params);
    [STORAGE_LOCATION_PARAM, MATERIAL_PARAM, AVV_PARAM, LABEL_PARAM].forEach((name) => rest.delete(name));
    return rest;
}

/** The request with the conditions added, all of which have to match. */
// @ai-generated 2026-10-26T11:20:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function withConditions(request: ListRequest, conditions: FilterCondition[]): ListRequest {
    if (conditions.length === 0) {
        return request;
    }
    const filters: FilterExpression['filters'] = request.filter_columns
        ? [request.filter_columns, ...conditions]
        : conditions;
    return { ...request, filter_columns: { operation: EnumFilterExpressionOperation.And, filters } };
}

const isNoAvvCondition = (condition: FilterCondition) =>
    condition.field === AVV_COLUMN && condition.value === NO_AVV_LABEL;

function mapExpression(expression: FilterExpression): FilterExpression {
    return {
        ...expression,
        filters: expression.filters.map((filter) => {
            if ('filters' in filter) {
                return mapExpression(filter);
            }
            return isNoAvvCondition(filter) ? { ...filter, value: '', operator: EnumOperation.Equal } : filter;
        }),
    };
}

/** The request with the "ohne AVV" column filter turned into an exact match on an empty AVV. */
// @ai-generated 2026-10-23T13:40:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function mapEmptyAvvFilter(request: ListRequest): ListRequest {
    if (!request.filter_columns) {
        return request;
    }
    return { ...request, filter_columns: mapExpression(request.filter_columns) };
}

/** The request sorted by creation after Zeitpunkt, the order in which the bookings of one instant count for the stock. */
// @ai-generated 2026-10-26T11:40:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function withCreationTieBreak(request: ListRequest): ListRequest {
    const [first, ...rest] = request.sort_columns ?? [];
    if (first?.column_name !== OCCURRED_AT_COLUMN || rest.some((sort) => sort.column_name === CREATED_AT_COLUMN)) {
        return request;
    }
    return {
        ...request,
        sort_columns: [first, ...rest, { column_name: CREATED_AT_COLUMN, direction: first.direction }],
    };
}
